import { AspectRatio } from '@lumen5/luminary';

import { MaxVideoDurationSeconds } from 'js/src/client';
import type { TypeMultiToggleItem } from 'js/src/components/common/ui/MultiToggle';

export const CLIP_CREATOR_LUMINARY_RESOLUTION = 215;
export const CLIP_CREATOR_PREVIEW_TEMPLATE = 'clip_creator_template';
export const CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE = 'clip_creator_4';
export const CLIP_CREATOR_TEMPLATE_BASE_SCENE_DESIGN = `${CLIP_CREATOR_PREVIEW_TEMPLATE}:talking_head_base_1`;
export const CLIP_CREATOR_TEMPLATE_OVERLAY_SCENE_DESIGN = `${CLIP_CREATOR_PREVIEW_TEMPLATE}:talking_head_overlay_1`;
export const CLIP_CREATOR_WAVEFORM_SCENE_DESIGN = `${CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE}:waveform_3`;
export const CAPTION_STYLE_NONE = 'caption_style_none';
export const CLIP_CREATOR_BLUEPRINT_NAME = 'Clip Creator Short Videos';
export const CLIP_CREATOR_DEFAULT_ASPECT_RATIO = AspectRatio.VERTICAL;
export const CLIP_CREATOR_DEFAULT_CAPTION_STYLE = CAPTION_STYLE_NONE;
export const WAVEFORM_STYLE_NONE = 'waveform_style_none';
export const CLIP_CREATOR_DEFAULT_WAVEFORM_STYLE = WAVEFORM_STYLE_NONE;
export const CLIP_DURATION_AUTO_KEY = 'auto';

export type ClipDurationOption = TypeMultiToggleItem & {
    minDuration: number | null;
    maxDuration: number | null;
};
export const CLIP_DURATION_OPTIONS: ClipDurationOption[] = [
    {
        key: CLIP_DURATION_AUTO_KEY,
        value: 'Auto',
        minDuration: null,
        maxDuration: null,
    },
    {
        key: '0',
        value: '<60 seconds',
        minDuration: 15,
        maxDuration: 60,
    },
    {
        key: '1',
        value: '60-90 seconds',
        minDuration: 60,
        maxDuration: 90,
    },
    {
        key: '2',
        value: '>90 seconds',
        minDuration: 90,
        maxDuration: MaxVideoDurationSeconds.LIMIT,
    },
];

/**
 * The Clip Creator accepts the following query string parameters:
 * - mediaId: The ID of the media to be used in the clip creator, if you want to skip the upload page.
 * - mediaType: The type of media ('video' or 'audio').
 * - skipContentPlanner: If set to 1, the user will be redirected directly to the create page, without showing the content planner.
 * - enableAllPages: If set to 1, all pages of the clip creator will be enabled, allowing navigation to any page without completing the previous ones.
 */
export enum ClipCreatorQueryParams {
    mediaId = 'mediaId',
    mediaType = 'mediaType',
    skipContentPlanner = 'skipContentPlanner',
    enableAllPages = 'enableAllPages',
}

// the sample clip is uploaded to Lumen5, then set `videoAsset.user = None; videoAsset.workspace = None; videoAsset.save();`
export const SAMPLE_CLIP_CREATOR_MEDIA_ID = 'f1ee3a60-9fb3-4585-b951-97e1e847a3f7';

export enum TrackProgressedPastUploadPageMethod {
    Upload = 'Upload',
    MediaLibrary = 'Media Library',
    SampleClip = 'Sample Clip',
    MediaIdFromQueryString = 'Media ID from query string',
}
export type TrackProgressedPastUploadPageOpts = {
    method: TrackProgressedPastUploadPageMethod;
};

export const progressedPastUploadPageEventName = 'Progressed past upload page';

export enum ClipMediaCrop {
    FILL_SCENE = 'fill_scene',
    FIT_TO_SCENE = 'fit_to_scene',
    SPLIT_SPEAKERS = 'split_speakers',
}

type ClipMediaCropOption = TypeMultiToggleItem & {
    key: ClipMediaCrop;
};
export const CLIP_MEDIA_CROP_OPTIONS: ClipMediaCropOption[] = [
    {
        key: ClipMediaCrop.FILL_SCENE,
        value: 'Fill scene',
    },
    {
        key: ClipMediaCrop.FIT_TO_SCENE,
        value: 'Fit to scene',
    },
    {
        key: ClipMediaCrop.SPLIT_SPEAKERS,
        value: 'Split speakers',
    },
];

export const ASPECT_RATIO_DATA_KEYS = [
    'vertical_data',
    'landscape_data',
    'square_data',
    'fourfive_data',
] as const;
